import React, { useEffect, useState } from "react";

import * as Yup from "yup";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";
import {
  MessageSquare,
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Loader2,
  ArrowRight,
  ArrowLeft,
  Building2,
  Check,
} from "lucide-react";

import AuthLayout from "../../components/AuthLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useWhiteLabel } from "../../context/WhiteLabel";

// O cadastro cria a empresa, o administrador dela e a assinatura. Por isso pede
// CPF/CNPJ, telefone, CEP e número: sem esses dados o Asaas recusa a cobrança e
// a conta nasceria sem como pagar. Como é muita coisa para uma tela só, vai em
// duas etapas — acesso primeiro, cobrança depois.

const somenteDigitos = (valor) => String(valor || "").replace(/\D/g, "");

const mascaraDocumento = (valor) => {
  const d = somenteDigitos(valor).slice(0, 14);

  if (d.length <= 11) {
    return d
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }

  return d
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const mascaraCep = (valor) =>
  somenteDigitos(valor)
    .slice(0, 8)
    .replace(/(\d{5})(\d)/, "$1-$2");

const mascaraTelefone = (valor) => {
  const d = somenteDigitos(valor).slice(0, 11);

  if (d.length <= 10) {
    return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  }

  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2");
};

const CadastroSchema = Yup.object().shape({
  adminName: Yup.string()
    .min(2, "Muito curto!")
    .max(50, "Muito longo!")
    .required("Obrigatório"),
  adminEmail: Yup.string().email("E-mail inválido").required("Obrigatório"),
  adminPassword: Yup.string()
    .min(5, "Mínimo de 5 caracteres")
    .required("Obrigatório"),
  companyName: Yup.string().min(2, "Muito curto!").required("Obrigatório"),
  document: Yup.string()
    .required("Obrigatório")
    .test("tamanho", "CPF ou CNPJ incompleto", (v) =>
      [11, 14].includes(somenteDigitos(v).length),
    ),
  email: Yup.string().email("E-mail inválido").required("Obrigatório"),
  phone: Yup.string()
    .required("Obrigatório")
    .test(
      "tamanho",
      "Telefone incompleto",
      (v) => somenteDigitos(v).length >= 10,
    ),
  postalCode: Yup.string()
    .required("Obrigatório")
    .test("tamanho", "CEP incompleto", (v) => somenteDigitos(v).length === 8),
  addressNumber: Yup.string().required("Obrigatório"),
});

// Campos da primeira etapa — validados sozinhos antes de avançar. Sem isso o
// botão "Continuar" reclamaria de campos que a pessoa ainda nem viu.
const CAMPOS_ETAPA_1 = ["adminName", "adminEmail", "adminPassword"];

const SignUp = () => {
  const navigate = useNavigate();
  const { appName, logoUrl } = useWhiteLabel();

  const [etapa, setEtapa] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [planos, setPlanos] = useState([]);
  const [planoEscolhido, setPlanoEscolhido] = useState(null);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const initialState = {
    adminName: "",
    adminEmail: "",
    adminPassword: "",
    companyName: "",
    document: "",
    email: "",
    phone: "",
    postalCode: "",
    address: "",
    addressNumber: "",
    addressComplement: "",
    province: "",
    city: "",
    state: "",
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/public-plans");
        setPlanos(Array.isArray(data) ? data : []);
      } catch (err) {
        // Sem planos à venda a tela continua funcionando: a empresa nasce sem
        // plano e o dono do SaaS define depois.
        setPlanos([]);
      }
    })();
  }, []);

  // Preenche rua, bairro e cidade a partir do CEP. É conveniência: se a
  // consulta falhar, os campos seguem editáveis e o cadastro não trava.
  const buscarCep = async (cep, setFieldValue) => {
    const digitos = somenteDigitos(cep);

    if (digitos.length !== 8) return;

    setBuscandoCep(true);

    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
      const dados = await resposta.json();

      if (!dados.erro) {
        setFieldValue("address", dados.logradouro || "");
        setFieldValue("province", dados.bairro || "");
        setFieldValue("city", dados.localidade || "");
        setFieldValue("state", dados.uf || "");
      }
    } catch (err) {
      // silencioso de propósito
    } finally {
      setBuscandoCep(false);
    }
  };

  const handleSignUp = async (values) => {
    try {
      await api.post("/auth/register-company", {
        ...values,
        document: somenteDigitos(values.document),
        phone: somenteDigitos(values.phone),
        postalCode: somenteDigitos(values.postalCode),
        planId: planoEscolhido,
      });
      toast.success(i18n.t("signup.toasts.success"));
      navigate("/login");
    } catch (err) {
      toastError(err);
    }
  };

  const avancar = async (validateForm, setTouched) => {
    const erros = await validateForm();
    const errosDaEtapa = CAMPOS_ETAPA_1.filter((campo) => erros[campo]);

    if (errosDaEtapa.length) {
      setTouched(
        CAMPOS_ETAPA_1.reduce((acc, campo) => ({ ...acc, [campo]: true }), {}),
      );
      return;
    }

    setEtapa(2);
  };

  const erroDe = (touched, errors, nome) =>
    touched[nome] && errors[nome] ? (
      <p className="text-xs text-destructive">{errors[nome]}</p>
    ) : null;

  return (
    <AuthLayout>
      {/* logo mobile */}
      <div className="mb-8 flex items-center gap-3 lg:hidden">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={appName}
            className="h-11 w-11 rounded-xl object-contain shadow-lg"
          />
        ) : (
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary shadow-lg shadow-primary/30">
            <MessageSquare className="h-6 w-6 text-white" />
          </div>
        )}
        <p className="text-lg font-bold tracking-tight">{appName}</p>
      </div>

      <div className="mb-6">
        <h1 className="text-3xl font-bold tracking-tight">Crie sua conta</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {etapa === 1
            ? "Comece agora a organizar seu atendimento no WhatsApp."
            : "Agora os dados da empresa, usados na sua fatura."}
        </p>
      </div>

      {/* As duas etapas ditas em voz alta: ninguém gosta de descobrir no meio
          que o formulário era maior do que parecia. */}
      <div className="mb-7 flex items-center gap-3">
        {[
          { numero: 1, rotulo: "Seu acesso" },
          { numero: 2, rotulo: "Sua empresa" },
        ].map((passo, indice) => (
          <React.Fragment key={passo.numero}>
            {indice > 0 && <span className="h-px flex-1 bg-border" />}
            <span className="flex items-center gap-2">
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                  etapa >= passo.numero
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground"
                }`}
              >
                {etapa > passo.numero ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  passo.numero
                )}
              </span>
              <span
                className={`text-xs font-medium ${
                  etapa === passo.numero
                    ? "text-foreground"
                    : "text-muted-foreground"
                }`}
              >
                {passo.rotulo}
              </span>
            </span>
          </React.Fragment>
        ))}
      </div>

      <Formik
        initialValues={initialState}
        validationSchema={CadastroSchema}
        onSubmit={(values, actions) => {
          handleSignUp(values).finally(() => actions.setSubmitting(false));
        }}
      >
        {({
          touched,
          errors,
          values,
          isSubmitting,
          setFieldValue,
          validateForm,
          setTouched,
        }) => (
          <Form className="space-y-5" noValidate>
            {etapa === 1 ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="adminName">Seu nome</Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="adminName"
                      name="adminName"
                      autoComplete="name"
                      autoFocus
                      placeholder="Seu nome completo"
                      className="h-11 pl-10"
                    />
                  </div>
                  {erroDe(touched, errors, "adminName")}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminEmail">E-mail de acesso</Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="adminEmail"
                      name="adminEmail"
                      type="email"
                      autoComplete="email"
                      placeholder="seu@email.com"
                      className="h-11 pl-10"
                    />
                  </div>
                  {erroDe(touched, errors, "adminEmail")}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="adminPassword">Senha</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="adminPassword"
                      name="adminPassword"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Mínimo de 5 caracteres"
                      className="h-11 pl-10 pr-11"
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {erroDe(touched, errors, "adminPassword")}
                </div>

                <Button
                  type="button"
                  onClick={() => avancar(validateForm, setTouched)}
                  className="h-11 w-full text-[15px] font-semibold"
                >
                  Continuar
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="companyName">Nome da empresa</Label>
                  <div className="relative">
                    <Building2 className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Field
                      as={Input}
                      id="companyName"
                      name="companyName"
                      autoFocus
                      placeholder="Como sua empresa se chama"
                      className="h-11 pl-10"
                    />
                  </div>
                  {erroDe(touched, errors, "companyName")}
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="document">CPF ou CNPJ</Label>
                    <Input
                      id="document"
                      name="document"
                      inputMode="numeric"
                      placeholder="000.000.000-00"
                      className="h-11"
                      value={values.document}
                      onChange={(e) =>
                        setFieldValue(
                          "document",
                          mascaraDocumento(e.target.value),
                        )
                      }
                    />
                    {erroDe(touched, errors, "document")}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      inputMode="numeric"
                      placeholder="(00) 00000-0000"
                      className="h-11"
                      value={values.phone}
                      onChange={(e) =>
                        setFieldValue("phone", mascaraTelefone(e.target.value))
                      }
                    />
                    {erroDe(touched, errors, "phone")}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-mail de cobrança</Label>
                  <Field
                    as={Input}
                    id="email"
                    name="email"
                    type="email"
                    placeholder="financeiro@suaempresa.com"
                    className="h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    É para onde a fatura vai. Pode ser o mesmo do seu acesso.
                  </p>
                  {erroDe(touched, errors, "email")}
                </div>

                <div className="grid gap-4 sm:grid-cols-[1fr_8rem]">
                  <div className="space-y-2">
                    <Label htmlFor="postalCode">CEP</Label>
                    <div className="relative">
                      <Input
                        id="postalCode"
                        name="postalCode"
                        inputMode="numeric"
                        placeholder="00000-000"
                        className="h-11"
                        value={values.postalCode}
                        onChange={(e) => {
                          const cep = mascaraCep(e.target.value);
                          setFieldValue("postalCode", cep);
                          buscarCep(cep, setFieldValue);
                        }}
                      />
                      {buscandoCep && (
                        <Loader2 className="absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                      )}
                    </div>
                    {erroDe(touched, errors, "postalCode")}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="addressNumber">Número</Label>
                    <Field
                      as={Input}
                      id="addressNumber"
                      name="addressNumber"
                      placeholder="123"
                      className="h-11"
                    />
                    {erroDe(touched, errors, "addressNumber")}
                  </div>
                </div>

                {(values.address || values.city) && (
                  <p className="-mt-1 text-xs text-muted-foreground">
                    {[values.address, values.province, values.city, values.state]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                )}

                <div className="space-y-2">
                  <Label htmlFor="addressComplement">
                    Complemento{" "}
                    <span className="font-normal text-muted-foreground">
                      (opcional)
                    </span>
                  </Label>
                  <Field
                    as={Input}
                    id="addressComplement"
                    name="addressComplement"
                    placeholder="Sala 4, fundos…"
                    className="h-11"
                  />
                </div>

                {planos.length > 0 && (
                  <div className="space-y-2">
                    <Label>Plano</Label>
                    <div className="grid gap-2">
                      {planos.map((plano) => (
                        <button
                          key={plano.id}
                          type="button"
                          onClick={() => setPlanoEscolhido(plano.id)}
                          className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                            planoEscolhido === plano.id
                              ? "border-primary bg-primary/5"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <span>
                            <span className="block text-sm font-medium">
                              {plano.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {plano.users} usuários · {plano.connections}{" "}
                              conexões
                            </span>
                          </span>
                          <span className="text-sm font-semibold">
                            R$ {Number(plano.value).toFixed(2).replace(".", ",")}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEtapa(1)}
                    className="h-11"
                    disabled={isSubmitting}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    className="h-11 flex-1 text-[15px] font-semibold"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        {i18n.t("signup.buttons.submit")}
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>

      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-background px-3 text-xs uppercase tracking-wider text-muted-foreground">
            Já tem uma conta?
          </span>
        </div>
      </div>

      <RouterLink to="/login" className="block">
        <Button type="button" variant="outline" className="h-11 w-full">
          Entrar na minha conta
        </Button>
      </RouterLink>
    </AuthLayout>
  );
};

export default SignUp;
